import { useDeliveryMethodModal } from '@b-stock/fe-core/bstock-next/components'
import type {
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { useIsSellerUserOfferingOwner } from '@/buyer-portal/components/features/listing/useIsSellerUserOfferingOwner'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import { useIsMobile } from '@/buyer-portal/hooks/layout'
import { redirectToAuth } from '@/helpers/auth'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useOfferAnalytics } from '../../offering/useOfferAnalytics'
import { ActionButton, ActionsBody, ChargeDisclaimer } from '../ui'
import { SimpleOfferQuickButtons } from './SimpleOfferQuickButtons'
import { SimpleOfferSubmit } from './SimpleOfferSubmit'

const ns = 'Listing.actionColumn.actionCard.offer'

export const SimpleOfferActions = ({
  listing,
  selectedQuote,
}: {
  listing: OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => {
  const mobileScreen = useIsMobile()
  const isLoggedIn = useIsLoggedIn()
  const isSellerUserOfferingOwner = useIsSellerUserOfferingOwner({
    offering: listing.offering,
  })
  const isServiceAccountOnly = useIsServiceAccountOnly()
  const { deliveryMethodChangePending } = useDeliveryMethodModal()
  const { trackButtonClicked } = useAnalytics()
  const getOfferAnalytics = useOfferAnalytics(listing)

  const size = mobileScreen ? 'medium' : 'large'
  const disabled =
    isSellerUserOfferingOwner ||
    isServiceAccountOnly ||
    deliveryMethodChangePending
  const hasQuote = !!selectedQuote

  // Guest — sign-in button
  if (!isLoggedIn) {
    return (
      <ActionsBody>
        <ActionButton
          appearance="primary"
          onClick={() => {
            // Track before redirect
            trackButtonClicked({
              buttonName: 'sign_in_to_offer',
              ...getOfferAnalytics(),
            })
            redirectToAuth()
          }}
          size={size}
          type="button"
        >
          <FormattedMessage id={`${ns}.actions.signIn`} />
        </ActionButton>
      </ActionsBody>
    )
  }

  // No delivery quote selected — the action routes to the delivery-method
  // modal, so there is nothing to make an offer with yet.
  if (!hasQuote) {
    return (
      <ActionsBody>
        <SimpleOfferSubmit disabled={disabled} listing={listing} />
      </ActionsBody>
    )
  }

  return (
    <ActionsBody>
      <SimpleOfferQuickButtons listing={listing} disabled={disabled} />

      <ChargeDisclaimer>
        <FormattedMessage id={`${ns}.actions.chargeNote`} />
      </ChargeDisclaimer>

      <SimpleOfferSubmit
        disabled={disabled}
        listing={listing}
        selectedQuote={selectedQuote}
      />
    </ActionsBody>
  )
}
